// front-end/src/components/content-editor-src/text-editor.js

import { getAuthToken } from '../auth';
import { apiPost } from '../../utils/api';
import { config } from '../../config/app-config';
import { createModal } from './ui-elements';
import { updatePendingChanges } from './content-store';
import { determinePageTitle } from '../../utils/page-utils';

export function openTextEditor(element, options = {}) {
  const pageTitle = determinePageTitle(element);
  const tagId = element.getAttribute('data-content');
  const contentType = element.getAttribute('data-type') || 'text';
  const blockName = element.getAttribute('data-block-name') || findBlockName(element);
  const dataAttribute = element.getAttribute('data-attribute') || determineDataAttribute(contentType);
  const isGlobal = element.getAttribute('data-global') === 'true';

  console.log(`Opening text editor for ${tagId}, determined page: ${pageTitle}`);

  const isHtml = dataAttribute === 'html';
  const rawContent = isHtml ? element.innerHTML.trim() : element.textContent.trim();
  // In textarea mode show <br> as real line breaks so the user sees them naturally
  const currentContent = (contentType === 'textarea' && isHtml)
    ? rawContent.replace(/<br\s*\/?>/gi, '\n')
    : rawContent;

  let modalContent;

  if (contentType === 'textarea') {
    const listBtn = isHtml
      ? `<button type="button" id="insert-list-btn" style="margin-bottom:8px;padding:4px 10px;font-size:12px;border:1px solid #ccc;border-radius:6px;cursor:pointer;background:#f5f5f5;">+ Список</button>`
      : '';
    modalContent = `
      <h3>Edit Content</h3>
      ${listBtn}
      <textarea id="content-input" rows="5" placeholder="Enter content">${currentContent}</textarea>
      ${blockName ? `<div class="block-info">Block: ${blockName}</div>` : ''}
      ${pageTitle !== 'Home' ? `<div class="page-info">Page: ${pageTitle}</div>` : ''}
      ${isGlobal ? `<div class="global-info">⚠️ This is global content (available on all pages)</div>` : ''}
    `;
  } else {
    modalContent = `
      <h3>Edit Content</h3>
      <input type="text" id="content-input" value="${currentContent}" placeholder="Enter content">
      ${blockName ? `<div class="block-info">Block: ${blockName}</div>` : ''}
      ${pageTitle !== 'Home' ? `<div class="page-info">Page: ${pageTitle}</div>` : ''}
      ${isGlobal ? `<div class="global-info">⚠️ This is global content (available on all pages)</div>` : ''}
    `;
  }

  const modal = createModal(modalContent);

  // Add styles for page-info
  const pageInfo = modal.element.querySelector('.page-info');
  if (pageInfo) {
    pageInfo.style.cssText = `
      color: #2196F3;
      font-size: 12px;
      margin-top: 5px;
      padding: 6px;
      background-color: #e3f2fd;
      border-radius: 4px;
    `;
  }

  // Add styles for global-info
  const globalInfo = modal.element.querySelector('.global-info');
  if (globalInfo) {
    globalInfo.style.cssText = `
      color: #ff9800;
      font-size: 12px;
      margin-top: 10px;
      padding: 8px;
      background-color: #fff3e0;
      border-radius: 4px;
    `;
  }

  const contentInput = document.getElementById('content-input');
  contentInput.focus();
  contentInput.select();

  const insertListBtn = document.getElementById('insert-list-btn');
  if (insertListBtn) {
    insertListBtn.addEventListener('click', () => {
      const listTemplate = `<ul>\n  <li>Пункт 1</li>\n  <li>Пункт 2</li>\n  <li>Пункт 3</li>\n</ul>`;
      const start = contentInput.selectionStart;
      const end = contentInput.selectionEnd;
      const val = contentInput.value;
      const prefix = val.slice(0, start) ? val.slice(0, start) + '\n' : '';
      contentInput.value = prefix + listTemplate + val.slice(end);
      contentInput.selectionStart = contentInput.selectionEnd = prefix.length + listTemplate.length;
      contentInput.focus();
    });
  }

  modal.on('save', () => {
    const newContent = contentInput.value.trim();
    if (newContent) {
      saveTextChange(element, {
        pageTitle,
        tagId,
        content: newContent,
        blockName,
        dataAttribute,
        isGlobal
      });
    }
    modal.close();
  });
}

export function saveTextChange(element, data) {
  console.log(`Saving text change for ${data.tagId} on page: ${data.pageTitle}`);

  updatePendingChanges('text', data.tagId, {
    pageTitle: data.pageTitle,
    tagId: data.tagId,
    content: data.content,
    blockName: data.blockName,
    dataAttribute: data.dataAttribute,
    isGlobal: data.isGlobal
  });

  if (data.dataAttribute === 'html') {
    element.innerHTML = data.content;
  } else {
    element.textContent = data.content;
  }
  element.classList.add('content-modified');

  const publishBtn = document.getElementById(config.editor.selectors.publishButton);
  if (publishBtn) {
    publishBtn.style.display = 'block';
  }
}

export async function publishTextChange(tagId, change) {
  const token = getAuthToken();
  if (!token) {
    return Promise.reject(new Error('Authentication required'));
  }

  const element = document.querySelector(`[data-content="${tagId}"]`);
  if (!element) {
    console.error(`Element with tagId ${tagId} not found`);
    return Promise.reject(new Error(`Element with tagId ${tagId} not found`));
  }

  const hasBlockName = change.blockName && change.blockName.trim() !== '';

  const requestBody = {
    page_title: change.isGlobal ? '' : change.pageTitle,
    tag_id: change.tagId,
    tag: change.tagId,
    content: change.content,
    order: 1,
    data_attribute: change.dataAttribute || 'text'
  };

  console.log(`Publishing text change for ${tagId}:`, requestBody);

  // For block content, add block_name if block exists
  if (hasBlockName && !change.isGlobal) {
    const blockElement = document.querySelector(`[data-block-name="${change.blockName}"]`);
    const blockId = blockElement ? blockElement.getAttribute('data-block-id') : null;

    if (blockId) {
      requestBody.block_name = change.blockName;
    } else {
      // For non-published blocks, still add block_name
      requestBody.block_name = change.blockName;
    }
  }

  try {
    const result = await apiPost(config.api.paths.content, requestBody);

    element.setAttribute('data-original-content', change.content);
    element.classList.remove('content-modified');

    console.log(`✅ Text change published successfully for ${tagId} on page ${change.pageTitle}`);
    return tagId;
  } catch (error) {
    console.error(`❌ Failed to publish text change for ${tagId}:`, error);
    throw error;
  }
}

function determineDataAttribute(contentType) {
  switch (contentType) {
    case 'textarea':
      return 'html';
    default:
      return 'text';
  }
}

function findBlockName(element) {
  let parent = element.parentElement;
  while (parent) {
    if (parent.hasAttribute && parent.hasAttribute('data-block-name')) {
      return parent.getAttribute('data-block-name');
    }
    parent = parent.parentElement;
  }
  return '';
}